<script setup lang="ts">
import { computed, h, type VNode } from "vue";
import { useRouter } from "vue-router";

import { SearchRouteHelper } from "@/helpers";
import { useUserStore } from "@/stores";

const props = defineProps<{
  text: string;
  language: string;
}>();

const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);

const searchQuery = computed<string>(() => searchRouteHelper.getSearchWord());

const HighlightedText = computed<VNode>(() => {
  // Exact match -> "un acrobate"
  if (isEqual(props.text)) {
    return createHighlightTextElement(props.text);
  }

  // Exact match with spanish ¡! -> "¡plazca!" or "¡no plazca!"
  if (props.text.startsWith("¡") || props.text.endsWith("!")) {
    const strippedText = props.text.replace(/¡|!/g, "");

    if (isEqual(strippedText)) {
      const prefix = props.text.startsWith("¡") ? "¡" : "";
      const suffix = props.text.endsWith("!") ? "!" : "";

      return createHighlightTextElement(strippedText, prefix, "", suffix);
    }
  }

  const text = props.text;

  const elements = text.split(/(\s+)/).map((word) => {
    // Keep spacing when splitting
    if (word === " ") {
      return " ";
    }

    // Exact match of splitted part -> "acrobates"
    if (isEqual(word)) {
      return createHighlightTextElement(word);
    }

    // Check for match of splitted with ' -> "l'acrobate"
    const wordParts = word.split("'");
    for (let i = 0; i < wordParts.length; i++) {
      // The i > 0 check is to avoid incorrect highlight of english words like "horse's"
      if (isEqual(wordParts[i]) && i > 0) {
        return createHighlightTextElement(wordParts[i], wordParts[0], "'");
      }
    }

    // Check for match with Spanish ¡! -> "¡no >plazca<!"
    if (word.startsWith("¡") || word.endsWith("!")) {
      const strippedWord = word.replace(/¡|!/g, "");

      if (isEqual(strippedWord)) {
        const prefix = word.startsWith("¡") ? "¡" : "";
        const suffix = word.endsWith("!") ? "!" : "";

        return createHighlightTextElement(strippedWord, prefix, "", suffix);
      }
    }

    return word;
  });

  // Must have a root node for computed render elements
  return h("span", elements);
});

const isEqual = (text: string): boolean =>
  text.localeCompare(searchQuery.value, props.language, { sensitivity: "base" }) === 0;

const createHighlightTextElement = (text: string, prefix?: string, prefixSeparator = "", suffix?: string): VNode => {
  const children = [];

  if (prefix) {
    children.push(prefix + prefixSeparator);
  }

  children.push(h("span", { class: "le-inflection-scheme-highlight" }, text));

  if (suffix) {
    children.push(suffix);
  }

  return h("span", children);
};
</script>

<template>
  <HighlightedText />
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-inflection-scheme-highlight {
  padding: 2px;
  border-radius: 2px;
  background-color: @colors[@accent][300];
}
</style>
